import { useEffect, useState } from 'react'
import { Trophy } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { toast } from 'sonner'
import { DatetimeLocalField } from '../DatetimeLocalField'
import { toDatetimeLocalValue } from '../reuniaoMeetingUtils'

const MIN_OBSERVACAO_LENGTH = 10

export type WinDealUpdates = {
  dataFechamento: string
  observacoes: string
}

export function WinDealModal({
  open,
  onClose,
  dealId: _dealId,
  defaultDate,
  defaultObservacoes,
  onConfirm,
  saving = false,
}: {
  open: boolean
  onClose: () => void
  dealId: string
  defaultDate?: string
  defaultObservacoes?: string
  onConfirm: (updates: WinDealUpdates) => void | Promise<void>
  saving?: boolean
}) {
  const [dataFechamento, setDataFechamento] = useState('')
  const [observacoes, setObservacoes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setDataFechamento(
      defaultDate ? toDatetimeLocalValue(defaultDate) : toDatetimeLocalValue(new Date().toISOString()),
    )
    setObservacoes(defaultObservacoes?.trim() ?? '')
  }, [open, defaultDate, defaultObservacoes])

  const handleConfirm = async () => {
    if (!dataFechamento) {
      toast.error('Informe a data do ganho')
      return
    }
    const trimmedObservacoes = observacoes.trim()
    if (trimmedObservacoes.length < MIN_OBSERVACAO_LENGTH) {
      toast.error('Descreva o projeto vendido (mínimo 10 caracteres)')
      return
    }
    setSubmitting(true)
    try {
      await onConfirm({
        dataFechamento: new Date(dataFechamento).toISOString(),
        observacoes: trimmedObservacoes,
      })
      onClose()
    } catch {
      toast.error('Erro ao marcar como ganho')
    } finally {
      setSubmitting(false)
    }
  }

  const isSaving = saving || submitting

  return (
    <Modal open={open} onClose={onClose} title="Marcar como Ganho" size="sm">
      <div className="space-y-4">
        <p className="text-xs text-text-label">
          Informe a data em que o negócio foi fechado e descreva o projeto vendido. Você pode
          escolher datas passadas para atualizar o histórico desde o início do ano.
        </p>

        <DatetimeLocalField
          value={dataFechamento}
          onChange={setDataFechamento}
          placeholder="Data e hora do ganho"
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
            Observação do projeto vendido *
          </label>
          <textarea
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            disabled={isSaving}
            rows={4}
            placeholder="Detalhe escopo, serviços contratados, prazos e condições acordadas..."
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-md px-3 py-2 outline-none focus:border-accent resize-none"
          />
          <p className="text-[10px] text-text-muted">
            Obrigatório para o time de onboarding e financeiro entender o que foi vendido.
          </p>
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSaving} className="flex-1">
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<Trophy size={13} />}
            loading={isSaving}
            onClick={handleConfirm}
            className="flex-1"
          >
            Confirmar Ganho
          </Button>
        </div>
      </div>
    </Modal>
  )
}
